import { useState, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Calendar, Search, Clock } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import CrmActivitiesService from '@/api/crmActivities.service';
import CustomerService from '@/api/customers.service';
import UserService from '@/api/users.service';
 
interface CRMActivity {
  id?: number;
  publicId: string;
  customerId?: number;
  companyId?: number;
  businessId: number;
  activityType: string;
  title: string;
  description?: string;
  date: string | Date;
  time?: string;
  priority?: 'low' | 'medium' | 'high';
  status?: 'planned' | 'completed' | 'canceled';
  assignedTo?: string;
  followUpDate?: string | Date;
  createdBy: number;
  dateCreated?: string | Date;
  dateUpdated?: string | Date;
  customer?: any; // This will contain customer details when populated
  company?: any; // This will contain company details when populated
}

interface ActivityFormProps {
  onSuccess?: () => void;
  initialCustomerId?: number;
  initialCompanyId?: number;
  activity?: CRMActivity;
}

export function ActivityForm({ onSuccess, initialCustomerId, initialCompanyId, activity }: ActivityFormProps) {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [users, setUsers] = useState<any[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  
  // Activity state
  const [title, setTitle] = useState(activity?.title || '');
  const [description, setDescription] = useState(activity?.description || '');
  const [activityType, setActivityType] = useState(activity?.activityType || '');
  const [activityDate, setActivityDate] = useState<Date | undefined>(activity?.date ? new Date(activity.date) : new Date());
  const [activityTime, setActivityTime] = useState(activity?.time || '');
  const [priority, setPriority] = useState<'low' | 'medium' | 'high'>(
    (activity?.priority as 'low' | 'medium' | 'high') || 'medium'
  );
  const [status, setStatus] = useState<'planned' | 'completed' | 'canceled'>(
    (activity?.status as 'planned' | 'completed' | 'canceled') || 'planned'
  );
  const [followUpDate, setFollowUpDate] = useState<Date | undefined>(
    activity?.followUpDate ? new Date(activity.followUpDate) : undefined
  );
  const [customerId, setCustomerId] = useState<number | null>(initialCustomerId || activity?.customerId || null);
  const [companyId, setCompanyId] = useState<number | null>(initialCompanyId || activity?.companyId || null);
  const [selectedCustomerName, setSelectedCustomerName] = useState('');
  const [assignedTo, setAssignedTo] = useState<string | undefined>(activity?.assignedTo || '0');

  const [selectedBusiness] = useState(() => {
    const storedBusiness = typeof window !== 'undefined' ? window.localStorage.getItem('selectedBusiness') : null;
    return storedBusiness ? JSON.parse(storedBusiness) : null;
  });

  useEffect(() => {
    if (initialCustomerId) {
      setCustomerId(initialCustomerId);
    }
    if (initialCompanyId) {
      setCompanyId(initialCompanyId);
    }
  }, [initialCustomerId, initialCompanyId]);

  // Fetch customer name if we have an ID
  useEffect(() => {
    if (customerId && !selectedCustomerName) {
      const fetchCustomerDetails = async () => {
        try {
          const response = await CustomerService.getCustomer(String(customerId));
          if (response.data.result === 'success') {
            const customer = response.data.customer;
            setSelectedCustomerName(`${customer.firstName} ${customer.lastName}`);
          }
        } catch (error) {
          console.error('Error fetching customer details:', error);
        }
      };
      
      fetchCustomerDetails();
    }
  }, [customerId, selectedCustomerName]);

  // Fetch users for dropdown
  useEffect(() => {
    if (selectedBusiness?.id) {
      const fetchUsers = async () => {
        setIsLoadingUsers(true);
        try {
          const response = await UserService.getAllUsers(selectedBusiness.id);
          if (response.data.result === 'success') {
            console.log("response.data.users", response.data.users)
            setUsers(response.data.users || []);
          }
        } catch (error) {
          console.error('Error fetching users:', error);
        } finally {
          setIsLoadingUsers(false);
        }
      };
      
      fetchUsers();
    }
  }, [selectedBusiness]);

  const searchCustomers = async () => {
    if (!customerSearchTerm || customerSearchTerm.length < 2) return;
    
    setIsSearching(true);
    try {
      const response = await CustomerService.searchCustomer(selectedBusiness.id, customerSearchTerm);
      if (response.data.result === 'success') {
        setSearchResults(response.data.customers || []);
      }
    } catch (error) {
      console.error('Error searching customers:', error);
    } finally {
      setIsSearching(false);
    }
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (customerSearchTerm.length >= 2) {
        searchCustomers();
      }
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [customerSearchTerm]);

  const validateFields = () => {
    if (!title.trim()) {
      toast({ title: "Error", description: "Title is required", variant: "destructive" });
      return false;
    }
    if (!activityType) {
      toast({ title: "Error", description: "Activity type is required", variant: "destructive" });
      return false;
    }
    if (!activityDate) {
      toast({ title: "Error", description: "Date is required", variant: "destructive" });
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateFields() || !selectedBusiness) return;

    setIsSubmitting(true);
    try {
      const formattedDate = activityDate ? format(activityDate, 'yyyy-MM-dd') : '';
      const formattedFollowUpDate = followUpDate ? format(followUpDate, 'yyyy-MM-dd') : null;
      
      const activityData = {
        title,
        description,
        activityType,
        date: formattedDate,
        time: activityTime,
        priority,
        status,
        followUpDate: formattedFollowUpDate,
        customerId,
        companyId,
        businessId: selectedBusiness.id,
        assignedTo,
      };

      let response;
      if (activity?.publicId) {
        response = await CrmActivitiesService.updateActivity(activity.publicId, activityData);
      } else {
        response = await CrmActivitiesService.createActivity(selectedBusiness.id, activityData);
      }

      if (response.data.result === 'success') {
        toast({
          title: "Success",
          description: activity ? "Activity updated successfully" : "Activity created successfully",
        });
        if (onSuccess) onSuccess();
      } else {
        toast({
          title: "Error",
          description: response.data.message || "Something went wrong",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save activity",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const activityTypes = [
    { value: 'call', label: 'Call' },
    { value: 'email', label: 'Email' },
    { value: 'meeting', label: 'Meeting' },
    { value: 'note', label: 'Note' },
    { value: 'task', label: 'Task' },
  ];

  const priorityLevels = [
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
  ];

  const statusOptions = [
    { value: 'planned', label: 'Planned' },
    { value: 'completed', label: 'Completed' },
    { value: 'canceled', label: 'Canceled' },
  ];

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="title">Title</Label>
        <Input 
          id="title" 
          placeholder="Activity title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="activityType">Activity Type</Label>
        <Select value={activityType} onValueChange={(value) => setActivityType(value)}>
          <SelectTrigger>
            <SelectValue placeholder="Select type" />
          </SelectTrigger>
          <SelectContent>
            {activityTypes.map((type) => (
              <SelectItem key={type.value} value={type.value}>
                {type.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="priority">Priority</Label>
          <Select 
            value={priority} 
            onValueChange={(value: 'low' | 'medium' | 'high') => setPriority(value)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select priority" />
            </SelectTrigger>
            <SelectContent>
              {priorityLevels.map((p) => (
                <SelectItem key={p.value} value={p.value}>
                  {p.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="status">Status</Label>
          <Select 
            value={status} 
            onValueChange={(value: 'planned' | 'completed' | 'canceled') => setStatus(value)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select status" />
            </SelectTrigger>
            <SelectContent>
              {statusOptions.map((s) => (
                <SelectItem key={s.value} value={s.value}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="assignedTo">Assign To (Optional)</Label>
        <Select 
          value={assignedTo} 
          onValueChange={(value) => setAssignedTo(value ? String(value) : undefined)}
        >
          <SelectTrigger>
            <SelectValue placeholder="Assign to self" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="0">Assign to self</SelectItem>
            {users.map((user) => (
              <SelectItem key={user.publicId} value={user.publicId}>
                {user.firstName} {user.lastName}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {isLoadingUsers && <div className="text-sm text-muted-foreground">Loading users...</div>}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-2 md:col-span-1">
          <Label htmlFor="date">Date</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant={"outline"}
                className={cn(
                  "w-full pl-3 text-left font-normal",
                  !activityDate && "text-muted-foreground"
                )}
              >
                {activityDate ? (
                  format(activityDate, "PPP")
                ) : (
                  <span>Pick a date</span>
                )}
                <Calendar className="ml-auto h-4 w-4 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <CalendarComponent
                mode="single"
                selected={activityDate}
                onSelect={(date) => setActivityDate(date || undefined)}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>

        <div className="space-y-2 md:col-span-1">
          <Label htmlFor="time">Time (Optional)</Label>
          <div className="relative">
            <Input
              id="time"
              type="time"
              value={activityTime}
              onChange={(e) => setActivityTime(e.target.value)}
              className="pl-10"
            />
            <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          </div>
        </div>

        <div className="space-y-2 md:col-span-1">
          <Label htmlFor="followUpDate">Follow-up Date (Optional)</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant={"outline"}
                className={cn(
                  "w-full pl-3 text-left font-normal",
                  !followUpDate && "text-muted-foreground"
                )}
              >
                {followUpDate ? (
                  format(followUpDate, "PPP")
                ) : (
                  <span>Pick a follow-up date</span>
                )}
                <Calendar className="ml-auto h-4 w-4 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <CalendarComponent
                mode="single"
                selected={followUpDate}
                onSelect={(date) => setFollowUpDate(date || undefined)}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {!initialCustomerId && (
        <div className="space-y-2">
          <Label htmlFor="customer">Related Customer (Optional)</Label>
          <div className="space-y-2">
            <div className="flex space-x-2">
              <Input
                id="customer"
                placeholder="Search customers..."
                value={customerSearchTerm}
                onChange={(e) => setCustomerSearchTerm(e.target.value)}
                className="flex-1"
              />
              <Button type="button" variant="outline" onClick={searchCustomers} disabled={isSearching}>
                {isSearching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              </Button>
            </div>
            
            {searchResults.length > 0 && (
              <div className="border rounded-md max-h-32 overflow-y-auto">
                {searchResults.map((customer) => (
                  <div
                    key={customer.id}
                    className="p-2 hover:bg-muted cursor-pointer flex justify-between items-center"
                    onClick={() => {
                      setCustomerId(customer.id);
                      setSelectedCustomerName(`${customer.firstName} ${customer.lastName}`);
                      setCustomerSearchTerm('');
                      setSearchResults([]);
                    }}
                  >
                    <span>{customer.firstName} {customer.lastName}</span>
                    {customer.companyName && <span className="text-sm text-muted-foreground">{customer.companyName}</span>}
                  </div>
                ))}
              </div>
            )}
            
            {customerId && selectedCustomerName && (
              <div className="flex justify-between items-center p-2 bg-primary/10 rounded-md">
                <span>Selected customer: {selectedCustomerName}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setCustomerId(null);
                    setSelectedCustomerName('');
                  }}
                >
                  Clear
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="description">Description</Label>
        <Textarea 
          id="description"
          placeholder="Add details about this activity" 
          className="min-h-[100px]"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>

      <div className="flex justify-end space-x-2 pt-4">
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {activity ? 'Update Activity' : 'Create Activity'}
        </Button>
      </div>
    </form>
  );
} 